import React, { useState, useEffect, useCallback } from 'react';
import cn from 'classnames';
import cogoToast from 'cogo-toast';
import moment from 'moment';

import { faStar, faCopy, faExternalLinkAlt, faBan, faPowerOff, faSync, faUnlink } from '@fortawesome/pro-light-svg-icons';

import { SkeletonTable, SkeletonLines } from '../SpotlightsSkeleton';
import SpotlightsSubTable from '../SpotlightsSubTable';
import CreatorVipModal from '../CreatorVipModal';
import useCollapse from '../useCollapse';
import { SpotlightsActionMenu, SpotlightsSortMenu, SpotlightsFilterMenu, FilterField, FilterOption } from '../SpotlightsMenus';
import { getCreators, getCreatorDetail, setCreatorVip, setCreatorSpotlightsDisabled, recheckAdPlatformGrant, unlinkAdPlatformGrant } from '../../../api/spotlights';
import { fmtMoney, fmtMoney2, fmtInt, fmtPct, getThresholds } from '../../../helpers/spotlights_dashboard_helpers';

const COLUMNS = [
  { key: 'name', label: 'Creator', sortable: true },
  { key: 'spotlights', label: 'Spotlights', sortable: true, num: true },
  { key: 'spend', label: 'Spend on them', sortable: true, num: true },
  { key: 'earned', label: 'Earned', sortable: false, num: true },
  { key: 'accept', label: 'Accept %', sortable: false, num: true },
  { key: 'reject', label: 'Reject %', sortable: false, num: true },
  { key: 'firstSpotlight', label: 'First spotlight', sortable: true },
  { key: 'flags', label: 'Flags', sortable: false },
  { key: 'mode', label: 'Approval', sortable: false }
];

// Effective approval mode (instant = auto-approve all, manual, or not yet onboarded).
const APPROVAL_FILTERS = [
  { key: '', label: 'All' },
  { key: 'auto_all', label: 'Instant' },
  { key: 'manual', label: 'Manual' },
  { key: 'none', label: 'Not onboarded' }
];

const SORT_OPTIONS = COLUMNS.filter(c => c.sortable).map(c => ({ key: c.key, label: c.label }));

const SpotlightsCreators = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [mine, setMine] = useState(false);
  const [vip, setVip] = useState(false);
  const [approval, setApproval] = useState('');
  const [notLaunched, setNotLaunched] = useState(false);
  const [vipModalOpen, setVipModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState('spend');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await getCreators({
        query: search,
        mine: mine ? 'true' : '',
        vip: vip ? 'true' : '',
        approval,
        filter: notLaunched ? 'notLaunched' : '',
        sortBy,
        sortDir,
        page
      });
      setRows(r.creators || []);
      setTotal(r.total || 0);
    } catch (e) {
      setError(typeof e === 'string' ? e : 'Failed to load creators.');
    } finally {
      setLoading(false);
    }
  }, [search, mine, vip, approval, notLaunched, sortBy, sortDir, page]);

  useEffect(() => {
    load();
  }, [load]);

  const onSort = key => {
    if (!COLUMNS.find(c => c.key === key)?.sortable) return;
    if (sortBy === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortBy(key);
      setSortDir('desc');
    }
    setPage(0);
  };

  const submitSearch = e => {
    e.preventDefault();
    setPage(0);
    setSearch(query);
  };

  const onVipChanged = (userId, value) => {
    setRows(rows.map(r => (r.User_id === userId ? { ...r, isSpotlightVip: value } : r)));
  };

  const onDisabledChanged = (userId, disabled, reason) => {
    setRows(rows.map(r => (r.User_id === userId ? { ...r, spotlightsDisabled: disabled, spotlightsDisabledReason: reason || null } : r)));
  };

  const pages = Math.ceil(total / 50);

  return (
    <div className='spotlights-creators'>
      <div className='sl-controls'>
        <form onSubmit={submitSearch}>
          <input className='sl-search' type='text' placeholder='Search creators…' value={query} onChange={e => setQuery(e.target.value)} />
        </form>
        <SpotlightsFilterMenu
          count={(mine ? 1 : 0) + (vip ? 1 : 0) + (approval ? 1 : 0) + (notLaunched ? 1 : 0)}
          onClear={() => { setMine(false); setVip(false); setApproval(''); setNotLaunched(false); setPage(0); }}
        >
          <FilterField label='Approval mode'>
            {APPROVAL_FILTERS.map(f => (
              <FilterOption key={f.key} label={f.label} selected={approval === f.key} onClick={() => { setApproval(f.key); setPage(0); }} />
            ))}
          </FilterField>
          <FilterField label='Show'>
            <label>
              <input type='checkbox' checked={mine} onChange={e => { setMine(e.target.checked); setPage(0); }} /> My creators
            </label>
            <label>
              <input type='checkbox' checked={vip} onChange={e => { setVip(e.target.checked); setPage(0); }} /> VIP only
            </label>
            <label>
              <input type='checkbox' checked={notLaunched} onChange={e => { setNotLaunched(e.target.checked); setPage(0); }} /> Onboarded, no spotlights
            </label>
          </FilterField>
        </SpotlightsFilterMenu>
        <SpotlightsSortMenu options={SORT_OPTIONS} value={sortBy} dir={sortDir} onChange={(key, d) => { setSortBy(key); setSortDir(d); setPage(0); }} />
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ color: '#767676', fontSize: 13 }}>{fmtInt(total)} creators</span>
          <button className='sl-btn' onClick={() => setVipModalOpen(true)}>
            Make VIP
          </button>
        </div>
      </div>

      {vipModalOpen && <CreatorVipModal onClose={() => setVipModalOpen(false)} onDone={load} />}

      {error ? (
        <div className='sl-error'>{error}</div>
      ) : loading && !rows.length ? (
        <SkeletonTable rows={8} cols={COLUMNS.length + 2} />
      ) : (
        <div className={cn('sl-refreshable', { 'sl-refreshing': loading })}>
          <table className='sl-table'>
            <thead>
              <tr>
                <th className='no-sort' style={{ width: 24 }} />
                {COLUMNS.map(c => (
                  <th key={c.key} className={cn({ 'no-sort': !c.sortable, num: c.num })} onClick={() => onSort(c.key)}>
                    {c.label} {sortBy === c.key ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                  </th>
                ))}
                <th className='no-sort' style={{ width: 44 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map(c => (
                <CreatorRow key={c.User_id} creator={c} expanded={expanded === c.User_id} onToggle={() => setExpanded(expanded === c.User_id ? null : c.User_id)} onVipChanged={onVipChanged} onDisabledChanged={onDisabledChanged} />
              ))}
              {!rows.length && (
                <tr>
                  <td colSpan={COLUMNS.length + 2} className='sl-empty'>
                    No creators match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {pages > 1 && (
            <div className='sl-controls' style={{ justifyContent: 'center', marginTop: 16 }}>
              <button className='sl-btn secondary small' disabled={page === 0} onClick={() => setPage(page - 1)}>
                Prev
              </button>
              <span style={{ fontSize: 13 }}>
                Page {page + 1} of {pages}
              </span>
              <button className='sl-btn secondary small' disabled={page + 1 >= pages} onClick={() => setPage(page + 1)}>
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CreatorRow = ({ creator, expanded, onToggle, onVipChanged, onDisabledChanged }) => {
  const [savingVip, setSavingVip] = useState(false);
  const [savingDisabled, setSavingDisabled] = useState(false);
  const [savingTikTokGrant, setSavingTikTokGrant] = useState(false);
  const { mounted, closing } = useCollapse(expanded);

  const toggleVip = async () => {
    setSavingVip(true);
    try {
      await setCreatorVip(creator.User_id, !creator.isSpotlightVip);
      cogoToast.success(`${creator.isSpotlightVip ? 'Removed' : 'Added'} VIP for @${creator.creatorUsername || creator.User_id}`);
      onVipChanged(creator.User_id, !creator.isSpotlightVip);
    } catch (err) {
      cogoToast.error(typeof err === 'string' ? err : 'Failed to update VIP.');
    } finally {
      setSavingVip(false);
    }
  };

  // Kill switch: disabling pauses live spotlights, cancels pending requests, and blocks all new
  // spotlight activity for this creator until re-enabled. The optional reason is shown (appended to
  // a generic unavailable message) to brands and the creator.
  const toggleDisabled = async () => {
    const who = `@${creator.creatorUsername || creator.User_id}`;
    let reason;
    if (!creator.spotlightsDisabled) {
      reason = window.prompt(
        `Disable Spotlights for ${who}?\n\nThis pauses any live spotlights, cancels pending requests, and blocks new ones until re-enabled.\n\nOptional reason (shown to the brand & creator, appended to the generic message):`,
        ''
      );
      if (reason === null) return; // cancelled
    } else if (!window.confirm(`Re-enable Spotlights for ${who}? Spotlights the switch paused will resume.`)) {
      return;
    }

    setSavingDisabled(true);
    try {
      const nextDisabled = !creator.spotlightsDisabled;
      await setCreatorSpotlightsDisabled(creator.User_id, nextDisabled, reason || null);
      cogoToast.success(`${nextDisabled ? 'Disabled' : 'Re-enabled'} Spotlights for ${who}`);
      onDisabledChanged(creator.User_id, nextDisabled, reason || null);
    } catch (err) {
      cogoToast.error(typeof err === 'string' ? err : 'Failed to update the Spotlights kill switch.');
    } finally {
      setSavingDisabled(false);
    }
  };

  // TikTok BC grant ops: recheck forces detection past the cache (the pilot's curl-only step);
  // unlink pauses the creator's live TikTok ads and reclaims the BC slot.
  const recheckGrant = async () => {
    setSavingTikTokGrant(true);
    try {
      const r = await recheckAdPlatformGrant('tiktok', creator.User_id);
      if (r?.status === 'granted') {
        cogoToast.success(`TikTok grant: granted${r.handle ? ` — @${r.handle}` : ''}`);
      } else {
        cogoToast.info(`TikTok grant: ${r?.status || 'not started'}${r?.reason ? ` (${r.reason})` : ''}`);
      }
    } catch (err) {
      cogoToast.error(typeof err === 'string' ? err : 'Failed to recheck the TikTok grant.');
    } finally {
      setSavingTikTokGrant(false);
    }
  };

  const unlinkGrant = async () => {
    const who = `@${creator.creatorUsername || creator.User_id}`;
    if (!window.confirm(`Unlink ${who}'s TikTok grant?\n\nThis pauses their live TikTok ads, releases the TT_ACCOUNT asset from our Business Center (reclaiming its slot), and clears the grant — they'd have to re-scan the QR to run TikTok Spotlights again.`)) {
      return;
    }
    setSavingTikTokGrant(true);
    try {
      const r = await unlinkAdPlatformGrant('tiktok', creator.User_id);
      cogoToast.success(
        r?.hadGrant
          ? `Unlinked TikTok grant for ${who} — ${r.adsPaused || 0} ad(s) paused, asset ${r.assetUnlinked ? 'unlinked' : 'not unlinked'}`
          : `${who} has no TikTok grant to unlink`
      );
    } catch (err) {
      cogoToast.error(typeof err === 'string' ? err : 'Failed to unlink the TikTok grant.');
    } finally {
      setSavingTikTokGrant(false);
    }
  };

  const actions = [
    { label: creator.isSpotlightVip ? 'Remove VIP' : 'Make VIP', icon: faStar, disabled: savingVip, onClick: toggleVip },
    {
      label: creator.spotlightsDisabled ? 'Re-enable Spotlights' : 'Disable Spotlights',
      icon: creator.spotlightsDisabled ? faPowerOff : faBan,
      disabled: savingDisabled,
      onClick: toggleDisabled
    },
    { label: 'Recheck TikTok grant', icon: faSync, disabled: savingTikTokGrant, onClick: recheckGrant },
    { label: 'Unlink TikTok grant', icon: faUnlink, disabled: savingTikTokGrant, onClick: unlinkGrant },
    {
      label: 'Copy User ID',
      icon: faCopy,
      onClick: () => {
        navigator.clipboard?.writeText(String(creator.User_id));
        cogoToast.success('Copied User ID');
      }
    }
  ];

  return (
    <>
      <tr className='sl-row-clickable' onClick={onToggle}>
        <td>
          <span className={cn('sl-caret', { open: expanded })}>▸</span>
        </td>
        <td>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {creator.isSpotlightVip && <span className='sl-pill amber'>★ VIP</span>}
            {creator.creatorUsername ? `@${creator.creatorUsername}` : creator.creatorName || creator.User_id}
            {creator.spotlightCount === 0 && <span className='sl-pill amber'>No spotlights</span>}
          </div>
        </td>
        <td className='num'>{fmtInt(creator.spotlightCount)}</td>
        <td className='num'>{fmtMoney(creator.spend)}</td>
        <td className='num'>{fmtMoney2(creator.totalEarned)}</td>
        <td className='num'>{creator.requestsReceived ? fmtPct(creator.acceptanceRate) : '—'}</td>
        <td className='num'>{creator.requestsReceived ? fmtPct(creator.rejectionRate) : '—'}</td>
        <td>{creator.firstSpotlightAt ? moment(creator.firstSpotlightAt).format('MMM D, YYYY') : '—'}</td>
        <td>
          <CreatorFlags creator={creator} />
        </td>
        <td>
          {creator.spotlightApprovalMode === 'auto_all' ? (
            <span className='sl-pill green'>Instant</span>
          ) : creator.spotlightApprovalMode === 'manual' ? (
            <span className='sl-pill'>Manual</span>
          ) : (
            <span className='sl-pill amber'>Not onboarded</span>
          )}
        </td>
        <td onClick={e => e.stopPropagation()}>
          <SpotlightsActionMenu options={actions} />
        </td>
      </tr>
      {mounted && (
        <tr>
          <td colSpan={COLUMNS.length + 2} className={cn('sl-expanded', { closing })}>
            <CreatorDetail userId={creator.User_id} />
          </td>
        </tr>
      )}
    </>
  );
};

const CreatorFlags = ({ creator }) => {
  const thresholds = getThresholds();
  const rejectionAlert = creator.requestsReceived > 0 && (creator.rejectionRate || 0) > thresholds.rejectionRate;
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {creator.spotlightsDisabled && (
        <span className='sl-pill red' title={creator.spotlightsDisabledReason || 'Spotlights disabled (kill switch)'}>
          Spotlights disabled
        </span>
      )}
      {rejectionAlert && (
        <span className='sl-pill red' title={`Rejection rate ${fmtPct(creator.rejectionRate)}`}>
          High rejection
        </span>
      )}
    </div>
  );
};

const CreatorDetail = ({ userId }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    getCreatorDetail(userId)
      .then(d => active && setDetail(d))
      .catch(e => {
        if (!active) return;
        setDetail(false);
        window.ALERT.error(typeof e === 'string' ? e : 'Failed to load creator detail.');
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [userId]);

  if (loading) return <SkeletonLines count={5} />;
  if (!detail) return <div className='sl-error'>Could not load creator detail.</div>;

  return (
    <div>
      <div className='sl-funnel' style={{ marginBottom: 14 }}>
        <FunnelItem v={fmtMoney2(detail.totalEarned)} l='Total earned (spotlight)' />
        <FunnelItem v={fmtInt(detail.funnel.sent)} l='Requests received' />
        <FunnelItem v={fmtPct(detail.funnel.acceptanceRate)} l='Accept rate' />
        <FunnelItem v={fmtPct(detail.funnel.rejectionRate)} l='Reject rate' />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14, marginBottom: 14 }}>
        <MiniList title='Top brands by spotlight count' rows={detail.topBrandsBySpotlights} render={b => [b.brandName, `${fmtInt(b.spotlightCount)} · ${fmtMoney(b.spend)}`]} />
        <MiniList title='Top brands by earnings' rows={detail.topBrandsByEarnings} render={b => [b.brandName, fmtMoney2(b.earned)]} />
        <MiniList title='Auto-approved brands' rows={detail.autoApprovedBrands} render={b => [b.brandName, '']} />
      </div>

      {/* Connected Meta / social accounts used for branded-content spotlights */}
      <div style={{ marginBottom: 14 }}>
        <div className='sl-card-label'>Connected accounts</div>
        <div className='sl-meta-kv' style={{ marginTop: 6 }}>
          {(detail.connectedAccounts || []).map((a, i) => (
            <div className='sl-kv' key={i}>
              <span className='k'>
                {a.platform || 'account'}{' '}
                {a.isExpired ? <span className='sl-pill red'>expired</span> : <span className='sl-pill green'>connected</span>}
              </span>
              <span className='v'>{a.handle || '—'}</span>
              {a.lastContentAt && <span className='k' style={{ textTransform: 'none' }}>last content {moment(a.lastContentAt).fromNow()}</span>}
            </div>
          ))}
          {(!detail.connectedAccounts || !detail.connectedAccounts.length) && <div className='sl-empty' style={{ padding: 12 }}>No connected accounts.</div>}
        </div>
      </div>

      <SpotlightsSubTable
        spotlights={detail.spotlights}
        identityLabel='Brand'
        getIdentity={s => s.brandName}
        extraColumns={[{ key: 'earned', label: 'Earned', num: true, render: s => fmtMoney2(s.earned) }]}
        getActions={s =>
          [
            s.metaAdUrl && { label: 'View ad on Meta', icon: faExternalLinkAlt, onClick: () => window.open(s.metaAdUrl, '_blank', 'noopener') },
            {
              label: 'Copy Spotlight ID',
              icon: faCopy,
              onClick: () => {
                navigator.clipboard?.writeText(String(s.id));
                cogoToast.success('Copied Spotlight ID');
              }
            }
          ].filter(Boolean)
        }
      />
    </div>
  );
};

const FunnelItem = ({ v, l }) => (
  <div className='sl-funnel-item'>
    <div className='v'>{v}</div>
    <div className='l'>{l}</div>
  </div>
);

const MiniList = ({ title, rows, render }) => (
  <div className='sl-card'>
    <div className='sl-card-label'>{title}</div>
    <table className='sl-table' style={{ marginTop: 6 }}>
      <tbody>
        {(rows || []).map((r, i) => {
          const [a, b] = render(r);
          return (
            <tr key={r.Brand_id || i}>
              <td>{a}</td>
              <td className='num'>{b}</td>
            </tr>
          );
        })}
        {(!rows || !rows.length) && (
          <tr>
            <td className='sl-empty' style={{ padding: 12 }}>
              None
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

export default SpotlightsCreators;
